import { DwModalService } from '@devwareapps/devware-cap';
import { confirmFriendRemoval } from '../user-profile/services/confirm-friend-removal';
import { ProfileFriendsService, ProfileFriendsState } from '../user-profile/services/profile-friends.service';
import { StreakerDirectoryService } from './services/streaker-directory.service';
import { Component, OnDestroy } from '@angular/core';
import { combineLatest, concat, of, Observable, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, shareReplay, take, finalize } from 'rxjs/operators';
import { DirectoryUser, ProfileDirectoryService } from '../shared/services/profile-routing/profile-directory.service';
import { userProfilePath } from '../shared/services/profile-routing/profile-links';
import { StreakerCentralStateService } from './services/streaker-central-state.service';
import { CentralDataService } from './services/central-data.service';

@Component({ selector: 'app-central-teams-page', template: '<app-team-discovery [embedded]="true"></app-team-discovery>' })
export class CentralTeamsPageComponent {}

@Component({ selector: 'app-central-stats-page', template: `
<ng-template appCentralChildNav><nav *ngIf="state.state$ | async as vm" class="central-child-tabs" aria-label="Stats view"><button type="button" *ngFor="let item of visibleTabs(vm.mode)" [attr.aria-pressed]="selectedTab(vm.mode) === item.key" (click)="tab = item.key">{{ item.label }}</button></nav></ng-template>
<ng-container *ngIf="state.state$ | async as vm">
  <app-central-skeleton *ngIf="vm.loading" page="stats"></app-central-skeleton>
  <div *ngIf="vm.error" class="central-card central-message"><p role="alert">{{ vm.error }}</p><button type="button" (click)="state.refresh()">Try again</button></div>
  <ng-container *ngIf="!vm.loading && !vm.error">
    <app-badge-leaderboards *ngIf="selectedTab(vm.mode) === 'streakers' || selectedTab(vm.mode) === 'teams'" [embedded]="true" [teamView]="tab === 'teams'"></app-badge-leaderboards>
    <app-central-overall-stats *ngIf="selectedTab(vm.mode) === 'overall'"></app-central-overall-stats>
    <ng-container *ngIf="selectedTab(vm.mode) === 'challenge'">
    <p *ngIf="vm.mode !== 'after-streak' && vm.membership !== 'registered'" class="central-message">Register for this challenge to see your stats.</p>
    <app-central-stats *ngIf="vm.mode === 'after-streak' || vm.membership === 'registered'"></app-central-stats>
    </ng-container>
  </ng-container>
</ng-container>` })
export class CentralStatsPageComponent {
    tab = 'streakers';
    readonly tabs = [{key:'streakers',label:'Streakers'},{key:'teams',label:'Teams'},
        {key:'challenge',label:'Challenge'},{key:'overall',label:'Overall'}];
    visibleTabs(mode: string) { return mode === 'pre-streak' || mode === 'unknown' ? this.tabs.slice(2) : this.tabs; }
    selectedTab(mode: string): string { return this.visibleTabs(mode).some(item => item.key === this.tab) ? this.tab : 'challenge'; }
    constructor(public state: StreakerCentralStateService) {}
}

interface StreakersView { challengeName: string; challengeId: number; userId: number; users: DirectoryUser[]; error: string; loading: boolean; }

@Component({ selector: 'app-central-streakers', templateUrl: './central-streakers.component.html',
    styleUrls: ['./central-streakers.component.scss'], providers: [ProfileFriendsService] })
export class CentralStreakersComponent implements OnDestroy {
    search = '';
    searchVisible = false;
    limit = 24;
    addingId = 0;
    removingId = 0;
    actionError = '';
    actionMessage = '';
    private _writes = new Subscription();
    private _identityScope = '';
    readonly view$: Observable<StreakersView>;
    readonly friends$;
    readonly donations$;
    constructor(private state: StreakerCentralStateService, private directory: ProfileDirectoryService,
        private data: CentralDataService, public friends: ProfileFriendsService, extras: StreakerDirectoryService, private _dialogs: DwModalService) {
        const context$ = state.state$.pipe(map(vm => ({ challenge: vm.challenge, userId: vm.userId, loading: vm.loading, error: vm.error })),
            distinctUntilChanged((a, b) => a.challenge?.ChallengeDefinitionId === b.challenge?.ChallengeDefinitionId &&
                a.userId === b.userId && a.loading === b.loading && a.error === b.error));
        this.view$ = combineLatest([context$, data.changed$.pipe(startWith(null))]).pipe(switchMap(([context]) => {
            const challenge = context.challenge;
            const identityScope = JSON.stringify([context.userId, challenge?.ChallengeDefinitionId]);
            if (identityScope !== this._identityScope) {
                this._identityScope = identityScope; this.addingId = 0; this.removingId = 0; this.actionError = ''; this.actionMessage = '';
            }
            this.search = ''; this.limit = 24; this.searchVisible = false;
            const base = { challengeName: challenge?.Name || '', challengeId: challenge?.ChallengeDefinitionId || 0,
                userId: context.userId, users: [] as DirectoryUser[], error: '' };
            if (!challenge) { return of({ ...base, loading: context.loading, error: context.error }); }
            return concat(of({ ...base, loading: true }), directory.get(challenge.ChallengeDefinitionId, 'streaker').pipe(
                map(result => ({ ...base, users: result.users, loading: false })),
                catchError(() => of({ ...base, loading: false, error: 'Unable to load streakers. Please try again.' }))));
        }), shareReplay({bufferSize: 1, refCount: true}));
        this.friends$ = combineLatest([friends.state$, data.teamChanged$.pipe(startWith(null))]).pipe(switchMap(([status]) => {
            const base = {...status, teammateIds: [] as number[], teammateError: ''};
            if (status.loading || status.error || !status.enrollmentId) { return of(base); }
            return extras.teammates(status.enrollmentId).pipe(map(teammateIds => ({...base, teammateIds})),
                catchError(() => of({...base, teammateError: 'Unable to load teammates. Please try again.'})));
        }), shareReplay({bufferSize: 1, refCount: true}));
        this.donations$ = this.view$.pipe(switchMap(vm => {
            const base = {loading: false, error: '', anonymousAmount: 0, rows: [] as {person: DirectoryUser; amount: number}[]};
            if (vm.loading || vm.error || !vm.challengeId) { return of(base); }
            return concat(of({...base, loading: true}), extras.donations(vm.challengeId).pipe(map(rows => ({...base,
                anonymousAmount: rows.filter(row => row.userId === 0).reduce((total, row) => total + row.amount, 0),
                rows: rows.filter(row => row.userId > 0).map(row => ({person: vm.users.find(user => user.UserId === row.userId), amount: row.amount}))
                    .filter(row => !!row.person).slice(0, 10)})),
                catchError(() => of({...base, error: 'Donation totals are unavailable right now.'}))));
        }));
    }
    name(user: DirectoryUser): string { return [user.FirstName, user.LastName].filter(Boolean).join(' ') || 'Streaker'; }
    filtered(users: DirectoryUser[]): DirectoryUser[] {
        const term = this.search.trim().toLocaleLowerCase();
        return users.filter(user => !term || this.name(user).toLocaleLowerCase().includes(term))
            .sort((a, b) => this.name(a).localeCompare(this.name(b)) || a.UserProfileId - b.UserProfileId);
    }
    myFriends(users: DirectoryUser[], status: ProfileFriendsState & {teammateIds: number[]}): DirectoryUser[] {
        return users.filter(user => user.UserChallengeId === status.enrollmentId || !!this.friendChip(user, status))
            .sort((a, b) => Number(b.UserChallengeId === status.enrollmentId) - Number(a.UserChallengeId === status.enrollmentId)
                || this.name(a).localeCompare(this.name(b)) || a.UserProfileId - b.UserProfileId);
    }
    friendChip(user: DirectoryUser, status: ProfileFriendsState & {teammateIds: number[]}): string {
        if (user.UserChallengeId === status.enrollmentId) { return 'You'; }
        const friend = status.friends.some(row => row.FriendUserChallengeId === user.UserChallengeId);
        const teammate = status.teammateIds.includes(user.UserChallengeId);
        return friend && teammate ? 'Team Friend' : friend ? 'Friend' : teammate ? 'Team Member' : '';
    }
    canAdd(user: DirectoryUser, status: ProfileFriendsState): boolean {
        return !status.loading && !status.error && status.options.some(option => option.id === user.UserChallengeId);
    }
    addFriend(user: DirectoryUser, status: ProfileFriendsState): void {
        if (this.friends.saving || this.friends.uncertain || !this.canAdd(user, status)) { return; }
        const scope = this._identityScope;
        this.addingId = user.UserChallengeId; this.actionError = ''; this.actionMessage = '';
        this._writes.add(this.friends.add(status.options.find(option => option.id === user.UserChallengeId)).pipe(take(1)).subscribe(() => {
            if (scope !== this._identityScope) { return; }
            this.addingId = 0; this.actionMessage = 'Friend added.'; this.friends.refresh();
        }, () => { if (scope !== this._identityScope) { return; } this.addingId = 0; this.removingId = 0; this.actionError = 'Unable to confirm the friend was added. Check your friends before trying again.'; }));
    }
    isFriend(user: DirectoryUser, status: ProfileFriendsState): boolean {
        return status.friends.some(friend => friend.FriendUserChallengeId === user.UserChallengeId);
    }
    removeFriend(user: DirectoryUser, status: ProfileFriendsState): void {
        const friend = status.friends.find(row => row.FriendUserChallengeId === user.UserChallengeId);
        if (!friend || this.removingId || this.friends.saving || this.friends.uncertain) { return; }
        const scope = this._identityScope;
        this.removingId = user.UserChallengeId; this.actionError = ''; this.actionMessage = '';
        this._writes.add(confirmFriendRemoval(this._dialogs, this.friends, friend.UserChallengeFriendId, this.name(user),
            () => scope === this._identityScope).pipe(finalize(() => { if (scope === this._identityScope) { this.removingId = 0; } })).subscribe(() => {
                if (scope !== this._identityScope) { return; }
                this.actionMessage = 'Friend removed.'; this.friends.refresh();
            }, () => { if (scope === this._identityScope) { this.actionError = 'Unable to confirm the change. Check your friends before trying again.'; } }));
    }
    toggleSearch(): void { this.searchVisible = !this.searchVisible; if (!this.searchVisible) { this.search = ''; this.limit = 24; } }
    profileLink(user: DirectoryUser): string { return userProfilePath(user.ProfileSlug); }
    trackUser(index: number, user: DirectoryUser): number { return user.UserChallengeId; }
    retry(): void { this.directory.invalidate(); this.state.refresh(); this.friends.refresh(); }
    ngOnDestroy(): void { this._writes.unsubscribe(); }
}
